import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Icon,
  IconButton,
  TextField,
  Typography,
} from '@mui/material'; 
import { useTheme } from '@mui/material/styles';
import Select from 'react-select';
import axios from 'axios';
import EditTagModal from './editTagModal';
import { commonToast } from '@/components/commonFunctions/commonToast';
import { Toast } from '@/components/commonFunctions/toastMessages';
import { deleteConfirmSwal } from '@/components/customModal/commonDialog/deleteConfirmSwal';
import { useFileListsStyles } from './fileLists.styles';

const TagManagement = (props: any) => {
  const {
    conferenceId,
    isModerator,
    getParticipantTagList,
    getFileList,
    pageId,
    onClose,
  } = props;
  const theme = useTheme();
  const classes = useFileListsStyles(theme).tagManagement;
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState('');
  const [selectedTags, setSelectedTags] = useState<any>(null);
  const [selectTagThisSite, setSelectTagThisSite] = useState<any>(null);
  const [showModal, setShowModal] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [showAllTags, setShowAllTags] = useState(false);

  const handleAddTag = () => {
    try {
      if (newTag) {
        axios
          .post(`/api/visitor/media/tags`, {
            label: newTag,
            conferenceId: conferenceId,
          })
          .then((response) => {
            setTags([...tags, { value: response.data.id, label: newTag }]);
            setNewTag('');
            commonToast('success', Toast.createSuccess('Tag'));
          })
          .catch((err) => {
            console.log(err);
            commonToast('error', Toast.createError('Tag'));
          });
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleTagClick = (tag: any) => {
    setSelectedTags(tag);
    setShowModal(true);
  };

  const handleChange = (e: any) => {
    setSelectedTags({ ...selectedTags, label: e.target.value?.trim() });
  };

  const handleEditTag = () => {
    if (!selectedTags.label) return;
    const updatedTags = tags.map((tag: any) => {
      if (tag.value === selectedTags?.value) {
        return { ...tag, ...selectedTags };
      }
      return tag;
    });
    setTags(updatedTags);

    // Keep the "This site" dropdown selection in sync with the renamed tag
    setSelectTagThisSite((prev: any) =>
      Array.isArray(prev)
        ? prev.map((tag: any) =>
            tag.value === selectedTags?.value
              ? { ...tag, ...selectedTags }
              : tag
          )
        : prev
    );

    setUpdating(true);
    try {
      axios
        .put(`/api/visitor/media/tags/${selectedTags.value}`, {
          label: selectedTags.label,
        })
        .then(() => {
          setShowModal(false);
          getParticipantTagList && getParticipantTagList();
          getFileList && getFileList();
          commonToast('success', Toast.updateSuccess('Tag'));
        })
        .catch((err) => {
          console.log(err);
          commonToast('error', Toast.updateError('Tag'));
        })
        .finally(() => {
          setUpdating(false);
        });
    } catch (err) {
      console.log(err);
      setUpdating(false);
    }
  };

  const handleDeleteTag = async (tag?: any) => {
    const targetTag = tag ?? selectedTags;
    if (!targetTag) return;
    const isConfirmed = await deleteConfirmSwal({
      title: 'Delete tag',
      text: `Are you sure you want to delete "${targetTag.label}"?`,
    });
    if (!isConfirmed) return;
    try {
      axios
        .delete(`/api/visitor/media/${pageId}/tags/${targetTag.value}`)
        .then(() => {
          setTags(tags.filter((t: any) => t.value !== targetTag.value));
          // Keep the "This site" dropdown selection in sync with the deleted tag
          setSelectTagThisSite((prev: any) =>
            Array.isArray(prev)
              ? prev.filter((t: any) => t.value !== targetTag.value)
              : prev
          );
          setShowModal(false);
          getParticipantTagList && getParticipantTagList();
          getFileList && getFileList();
          commonToast('success', Toast.deleteSuccess('Tag'));
        })
        .catch((err) => {
          console.log(err);
          commonToast('error', Toast.deleteError('Tag'));
        });
    } catch (err) {
      console.log(err);
    }
  };

  const handleSaveSettings = () => {
    if (
      !showAllTags &&
      (!selectTagThisSite || selectTagThisSite?.length === 0)
    ) {
      commonToast(
        'warning',
        'Please select at least one tag or check "Show all tags"'
      );
      return;
    }
    setSavingSettings(true);
    try {
      axios
        .post(`/api/visitor/media/tags/settings/${conferenceId}/${pageId}`, {
          showAllTags: showAllTags,
          selectedTags: selectTagThisSite ? selectTagThisSite : null,
        })
        .then(() => {
          getParticipantTagList && getParticipantTagList();
          getFileList && getFileList();
          commonToast('success', Toast.updateSuccess('Settings'));
        })
        .catch((err) => {
          console.log(err);
          commonToast('error', Toast.updateError('Settings'));
        })
        .finally(() => {
          setSavingSettings(false);
        });
    } catch (err) {
      console.log(err);
      setSavingSettings(false);
    }
  };

  const handleSelectTagThisSite = (e: any) => {
    setSelectTagThisSite(e);
  };

  const getTags = () => {
    setLoading(true);
    axios
      .get(`/api/visitor/${conferenceId}/${pageId}/media/tags`)
      .then((response) => {
        console.log('response.data', response.data);
        setTags(
          response.data?.map((tag: any) => ({
            value: tag.id,
            label: tag.label,
          }))
        );
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getTags();
    return () => {};
  }, []);

  useEffect(() => {
    if (isModerator) {
      axios
        .get(`/api/visitor/media/tags/settings/${conferenceId}/${pageId}`)
        .then((response) => {
          console.log('response.data settings', response.data);
          setShowAllTags(response.data.showAllTags);
          setSelectTagThisSite(response.data.selectedTags);
        });
    }
  }, [isModerator]);

  return (
    <Box sx={classes.root}>
      {loading ? (
        <Box sx={classes.loaderWrap}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <>
          <Box sx={classes.headerRow}>
            <Typography variant="h6" fontWeight={700}>
              Add a tag
            </Typography>
            {onClose && (
              <IconButton size="small" onClick={onClose} aria-label="close">
                <Icon fontSize="small">close</Icon>
              </IconButton>
            )}
          </Box>

          <Box sx={classes.addTagRow}>
            <TextField
              size="small"
              variant="outlined"
              fullWidth
              placeholder="Tag name"
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              sx={classes.addTagInput}
            />
            <Button
              onClick={handleAddTag}
              variant="contained"
              color="primary"
              sx={classes.addTagButton}
            >
              Add tag
            </Button>
          </Box>

          <Typography
            variant="subtitle2"
            fontWeight={600}
            sx={classes.allTagsLabel}
          >
            All tags
          </Typography>
          <Box sx={classes.chipList}>
            {tags.map((tag: any) => (
              <Chip
                key={tag.value}
                label={tag.label}
                variant="outlined"
                onClick={() => handleTagClick(tag)}
                onDelete={() => handleDeleteTag(tag)}
                sx={classes.chip}
              />
            ))}
          </Box>

          <Box sx={classes.siteCard}>
            <Typography variant="subtitle1" fontWeight={600}>
              This site
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showAllTags}
                  onChange={(e) => setShowAllTags(e.target.checked)}
                />
              }
              label="Show all tags"
            />
            <Typography variant="body1" sx={classes.siteHint}>
              Choose which tags appear on this site
            </Typography>
            <Select
              isMulti
              value={selectTagThisSite}
              options={tags}
              placeholder="Choose tags"
              onChange={handleSelectTagThisSite}
              isDisabled={showAllTags}
              styles={classes.select}
            />
          </Box>

          <Box sx={classes.saveRow}>
            <Button
              variant="contained"
              color="primary"
              disabled={savingSettings}
              startIcon={
                savingSettings && (
                  <CircularProgress color="inherit" size={18} />
                )
              }
              onClick={() => handleSaveSettings()}
              sx={classes.saveButton}
            >
              {savingSettings ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </>
      )}

      {/* Modal for Editing & Deleting Tags */}
      {showModal && (
        <EditTagModal
          open={showModal}
          tag={selectedTags}
          updating={updating}
          onClose={() => setShowModal(false)}
          onChange={handleChange}
          onUpdate={handleEditTag}
        />
      )}
    </Box>
  );
};

export default TagManagement;
